// Guided create-benefit intake.
//
// One route with the applicant, the household, and income as steps, replacing the
// search-and-create dialog that produced an empty application and left staff to find the
// rest of the form themselves.
//
// The draft application row is created before the household and income steps, on purpose:
// those steps then write through exactly the same bounded endpoints, and the same section
// components, as the detail page tabs. There is no second "create payload" code path and no
// giant atomic submit, and abandoning the flow half-way leaves a resumable draft rather than
// losing the work.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { benefitCreationActions as creationActions, creationLocked, creationNavigationBlocked } from "@/features/benefit-workspace/benefitCreationSlice"
import { BenefitCreationStatus } from "@/features/benefit-workspace/BenefitCreationStatus"
import { useBenefitReadBridge } from "@/features/benefit-workspace/useBenefitReadBridge"
import { BenefitEditorWorkspace } from "@/features/benefit-workspace/BenefitEditorWorkspace"
import { workflowCanEdit } from "@/lib/benefit-workflow"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconEye,
  IconFilePlus,
  IconLoader2,
  IconPlayerPlay,
  IconSearch,
  IconUserPlus,
  IconUsersGroup,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"
import { ApplicationDetailsSection } from "@/components/benefit/application-details-section"
import { IntakeDetailsSection } from "@/components/benefit/intake-details-section"
import { HouseholdCopyNotice } from "@/components/benefit/household-copy-notice"
import { HouseholdCopyDialog, type HouseholdCopyConfirmation } from "@/components/benefit/household-copy-dialog"
import { BenefitEditScope, useHasOpenBenefitEditors } from "@/components/benefit/benefit-edit-scope"
import { HouseholdIncomeSection } from "@/components/benefit/household-income-section"
import { HouseholdInformationSection } from "@/components/benefit/household-information-section"
import { HouseholdMembersSection } from "@/components/benefit/household-members-section"
import { NonCashBenefitsSection, QuestionnaireSection } from "../detail/page"
import { TargetProgramYearSection } from "@/components/benefit/target-program-year-section"
import {
  PersonIdentityFields,
  PotentialDuplicates,
} from "@/components/people/person-identity-form"
import { applicationStateLabels, benefitAppTypeLabels, eligibilityLabels, selectOptions } from "@/lib/benefit-enums"
import { enumLabel, fmtDate, fmtMoney, isBenefitEditable } from "@/lib/benefit-format"
import type {
  BenefitDetailDto,
  BenefitNewRootCreationCapability,
} from "@/lib/benefit-types"
import { applicationWorkflowLabel } from "@/lib/benefit-workflow"
import {
  CREATE_BENEFIT_STEPS,
  createBenefitStepLabels,
  priorApplicationAction,
  resolveStep,
  stepIndex,
  type CreateBenefitStep,
} from "@/lib/create-benefit-flow"
import {
  canCheckDuplicates,
  duplicateQuery,
  createPersonPayload,
  displaySsn,
  formatPersonName,
  validatePersonIdentity,
} from "@/lib/person-identity"
import { useAppDispatch, useAppSelector, useAppStore } from "@/store"

function personName(person: { lastName: string; firstName: string; middleName?: string }): string {
  return formatPersonName(person)
}

function CreationCapabilityNotice({
  capability,
  loading,
}: {
  capability: BenefitNewRootCreationCapability | null
  loading: boolean
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          Checking the server-selected creation mode…
        </CardContent>
      </Card>
    )
  }

  if (!capability) return null

  const modeLabel = capability.mode === "V2"
    ? "V2 workflow"
    : capability.mode === "Legacy"
      ? "Compatibility mode"
      : "Creation unavailable"

  return (
    <Card className={capability.canCreate ? undefined : "border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-950/20"}>
      <CardContent className="flex flex-wrap items-start justify-between gap-3 py-4">
        <div>
          <p className="font-medium">Server-selected creation mode</p>
          <p className="text-sm text-muted-foreground">
            {capability.canCreate
              ? capability.mode === "V2"
                ? "New roots created here will use the versioned workflow authority."
                : "New roots created here will retain the existing compatibility authority."
              : capability.disabledReason ?? "The server did not authorize new-root creation."}
          </p>
        </div>
        <Badge variant={capability.canCreate ? "secondary" : "outline"}>{modeLabel}</Badge>
      </CardContent>
    </Card>
  )
}

function ImportedPseSourceNotice({ benefit }: { benefit: BenefitDetailDto }) {
  const source = benefit.importedPseSource
  if (!source) return null

  const hasPerPersonIncome = benefit.personCollection.some((person) => person.incomeCollection.length > 0)
  return (
    <Card className="border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-950/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Imported PSE source evidence</CardTitle>
        <CardDescription>
          Source values recorded at creation remain visible for reconciliation. They are not verified current CMS income or a new award.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div><p className="text-muted-foreground">Source program year</p><p className="font-medium">{source.programYear}</p></div>
        <div><p className="text-muted-foreground">Source monthly household income</p><p className="font-medium">{fmtMoney(source.monthlyIncome)}</p></div>
        <div><p className="text-muted-foreground">Source eligible amount</p><p className="font-medium">{fmtMoney(source.eligibilityAmount)}</p></div>
        <div><p className="text-muted-foreground">Source total paid</p><p className="font-medium">{fmtMoney(source.totalPaidAmount)}</p></div>
        {source.monthlyIncome > 0 && !hasPerPersonIncome && (
          <p className="sm:col-span-2 lg:col-span-4 font-medium text-amber-900 dark:text-amber-200">
            The imported household income has no per-person breakdown. Do not record this as verified zero income; collect and save each member's current income before calculating eligibility.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function StepIndicator({ current }: { current: CreateBenefitStep }) {
  const currentIndex = stepIndex(current)
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {CREATE_BENEFIT_STEPS.map((step, index) => {
        const state = index === currentIndex ? "current" : index < currentIndex ? "done" : "todo"
        return (
          <li key={step} className="flex items-center gap-2">
            <span
              className={
                state === "current"
                  ? "flex size-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                  : state === "done"
                    ? "flex size-6 items-center justify-center rounded-full bg-muted text-xs font-medium"
                    : "flex size-6 items-center justify-center rounded-full border text-xs text-muted-foreground"
              }
            >
              {state === "done" ? <IconCheck className="size-3.5" /> : index + 1}
            </span>
            <span className={state === "todo" ? "text-muted-foreground" : "font-medium"}>
              {createBenefitStepLabels[step]}
            </span>
            {index < CREATE_BENEFIT_STEPS.length - 1 && (
              <IconArrowRight className="size-4 text-muted-foreground" />
            )}
          </li>
        )
      })}
    </ol>
  )
}

function ApplicantStep({
  applicantId,
  onSelect,
  onClear,
}: {
  applicantId: string | null
  onSelect: (personId: string, liheapEligibility?: number) => void
  onClear: () => void
}) {
  const dispatch = useAppDispatch()
  const state = useAppSelector(s => s.benefitCreation)
  const session = useAppSelector(s => s.staffSession)
  const { mode, searchText, identity, liheapEligibility } = state
  const creating = creationLocked(state)
  const setMode = (mode: "find" | "create") => dispatch(creationActions.fieldsChanged({ mode }))
  const setSearchText = (searchText: string) => dispatch(creationActions.fieldsChanged({ searchText }))
  const setIdentity = (identity: typeof state.identity) => dispatch(creationActions.fieldsChanged({ identity }))
  const setLiheapEligibility = (liheapEligibility: string) => dispatch(creationActions.fieldsChanged({ liheapEligibility }))
  const personRead = benefitApi.useGetBenefitCreationPersonQuery(session.scopeKey && applicantId ? { scopeKey: session.scopeKey, personId: applicantId } : skipToken)
  const applicant = personRead.currentData
  const searchRead = benefitApi.useSearchBenefitPeopleQuery(session.scopeKey && state.submittedSearch ? { scopeKey: session.scopeKey, searchText: state.submittedSearch } : skipToken)
  const results = searchRead.currentData?.matches ?? []
  const searching = searchRead.isFetching
  const hasSearched = !!state.submittedSearch
  const duplicateKey = mode === "create" && canCheckDuplicates(identity) ? duplicateQuery(identity) : ""
  const [debouncedKey, setDebouncedKey] = useState("")
  useEffect(() => { const timer = setTimeout(() => setDebouncedKey(duplicateKey), 400); return () => clearTimeout(timer) }, [duplicateKey])
  const duplicateRead = benefitApi.useGetBenefitPotentialDuplicatesQuery(session.scopeKey && duplicateKey && duplicateKey === debouncedKey
    ? { scopeKey: session.scopeKey, query: duplicateKey } : skipToken)
  const duplicates = duplicateKey === debouncedKey ? duplicateRead.currentData?.matches ?? [] : []
  const checking = !!duplicateKey && (duplicateKey !== debouncedKey || duplicateRead.isFetching || duplicateRead.isUninitialized)
  const search = () => {
    if (creating || !searchText.trim()) return
    if (state.submittedSearch === searchText.trim()) void searchRead.refetch()
    else dispatch(creationActions.fieldsChanged({ submittedSearch: searchText.trim() }))
  }
  const create = () => {
    if (creating || !session.scopeKey || checking) return
    const invalid = validatePersonIdentity(identity)
    if (invalid) { toast.error(invalid); return }
    if (duplicates.length > 0) { toast.error("Resolve the potential duplicate(s) before creating a new person."); return }
    // Keep the established optional duplicate-read failure policy; the server still validates creation.
    dispatch(creationActions.attemptRequested({ id: crypto.randomUUID(), scopeKey: session.scopeKey, epoch: session.epoch,
      kind: "person", body: createPersonPayload(identity), liheapEligibility: Number(liheapEligibility), phase: "saving", resultId: null }))
  }

  if (applicantId) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Applicant</CardTitle>
          <CardDescription>The head of household this application belongs to</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-medium">{applicant ? personName(applicant) : "Loading…"}</p>
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{displaySsn(applicant?.socialSecurityNumber)}</span>
              {applicant?.birthDate ? ` • ${fmtDate(applicant.birthDate)}` : ""}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClear} disabled={creating}>
            Change applicant
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Find or create the applicant</CardTitle>
        <CardDescription>
          Search first. Creating a second record for someone who already exists splits their
          benefit history across two files.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={mode} onValueChange={(value) => setMode(value as "find" | "create")}>
          <TabsList>
            <TabsTrigger value="find" disabled={creating}>Find a person</TabsTrigger>
            <TabsTrigger value="create" disabled={creating}>Create a person</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === "find" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                disabled={creating}
                placeholder="Search by name, SSN, or partial SSN…"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && search()}
              />
              <Button onClick={search} disabled={creating || searching || !searchText.trim()}>
                {searching ? <IconLoader2 className="size-4 animate-spin" /> : <IconSearch className="size-4" />}
                Search
              </Button>
            </div>
            {searchRead.isError && <p role="alert">Search failed. Search again to retry.</p>}
            <div className="max-h-[360px] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>SSN</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead className="w-[1%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((match) => (
                    <TableRow key={match.id}>
                      <TableCell className="font-medium">
                        {formatPersonName(match)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {displaySsn(match.socialSecurityNumber)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {match.dateOfBirth ? new Date(match.dateOfBirth).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" disabled={creating || searching} onClick={() => onSelect(match.id)}>
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {hasSearched && !searching && !searchRead.isError && results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No people found. Try different terms, or create the person.
                      </TableCell>
                    </TableRow>
                  )}
                  {!hasSearched && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        Search for the applicant to begin.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <PersonIdentityFields
              value={identity}
              onChange={setIdentity}
              disabled={creating}
              idPrefix="applicant"
            />
            <div className="space-y-1.5">
              <Label htmlFor="applicant-liheap-eligibility">LIHEAP Eligibility</Label>
              <Select value={liheapEligibility} disabled={creating} onValueChange={setLiheapEligibility}>
                <SelectTrigger id="applicant-liheap-eligibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {selectOptions(eligibilityLabels).map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <PotentialDuplicates
              duplicates={duplicates}
              checking={checking}
              selectLabel="Use the existing record as the applicant instead of creating a duplicate, or change the details if this is a different person."
              onSelect={(match) => { if (!creating) onSelect(match.id) }}
            />
            <Button onClick={create} disabled={creating || checking || duplicates.length > 0}>
              {creating ? <IconLoader2 className="size-4 animate-spin" /> : <IconUserPlus className="size-4" />}
              Create and use as applicant
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Step 2 — reconcile against what the applicant already has.
 *
 * An applicant with an open draft usually wants that draft, not a third empty file, and an
 * applicant renewing usually wants last year's household rather than typing it again. With
 * no prior applications, starting a root still requires a deliberate action. Reading an
 * empty list (including after remount/reconnect) must never replay creation.
 */
export function ExistingApplicationsStep({
  applicantId,
  canEditCompleted,
  onResume,
  onStartDraft,
  starting,
  creationCapability,
}: {
  applicantId: string
  canEditCompleted: boolean
  onResume: (applicationId: string) => void
  onStartDraft: (copyHouseholdFromBenefitApplicationId: string | null, confirmation?: HouseholdCopyConfirmation) => Promise<boolean>
  starting: boolean
  creationCapability: BenefitNewRootCreationCapability | null
}) {
  const canStart = creationCapability?.canCreate === true
  const dispatch = useAppDispatch()
  const state = useAppSelector(s => s.benefitCreation)
  const scopeKey = useAppSelector(s => s.staffSession.scopeKey)
  const history = benefitApi.useGetBenefitPersonApplicationsQuery(scopeKey ? { scopeKey, personId: applicantId } : skipToken, { refetchOnMountOrArgChange: true })
  const year = benefitApi.useGetBenefitApplicableYearQuery(scopeKey ? { scopeKey } : skipToken)
  const applications = history.currentData ?? (history.isError ? [] : null)
  const configuredYear = Number.isInteger(year.currentData?.programYear) && year.currentData!.programYear > 0 ? year.currentData!.programYear : null
  const configuredYearUnavailable = year.isError || (year.isSuccess && configuredYear === null)
  const showPreviousYears = state.previousYearsFor === applicantId
  const setShowPreviousYears = (show: boolean) => dispatch(creationActions.fieldsChanged({ previousYearsFor: show ? applicantId : null }))
  const copySourceId = state.copy?.applicantId === applicantId ? state.copy.sourceId : null
  const lastCopySource = useRef<string | null>(null)
  if (copySourceId) lastCopySource.current = copySourceId
  const returnCopyFocus = () => {
    const opener = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-copy-source]'))
      .find(button => button.dataset.copySource === lastCopySource.current && !button.disabled)
    ;(opener ?? document.querySelector<HTMLElement>('[data-creation-heading]'))?.focus()
  }
  const setCopySourceId = (sourceId: string | null) => sourceId
    ? dispatch(creationActions.copyOpened({ id: crypto.randomUUID(), sourceId, applicantId }))
    : dispatch(creationActions.copyClosed())
  const failed = history.isError
  // The draft host must remain reachable even if its source disappears from the history read.
  const copyDialog = <HouseholdCopyDialog sourceId={copySourceId} applicantId={applicantId} busy={starting}
    onClose={() => setCopySourceId(null)} onReturnFocus={returnCopyFocus} onConfirm={onStartDraft} />
  // Empty history is a read result, never a command trigger. Remount/reconnect cannot create a root.

  if (applications === null) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Checking for existing applications…</p>
        {copyDialog}
      </div>
    )
  }

  if (applications.length === 0) {
    if (creationCapability && !canStart) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <p className="text-center text-sm text-muted-foreground">
            {creationCapability.disabledReason ?? "The server did not authorize new-root creation."}
          </p>
          {copyDialog}
        </div>
      )
    }

    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-sm text-muted-foreground">
          {failed ? "Could not read this applicant's history. Check for an existing application before starting another."
            : "No existing applications. Start a new one when ready."}
        </p>
        {failed && <Button variant="outline" onClick={() => void history.refetch()}>Retry application history</Button>}
        <Button onClick={() => onStartDraft(null)} disabled={starting || !canStart}>
          Start a new application
        </Button>
        {copyDialog}
      </div>
    )
  }

  const visibleApplications = showPreviousYears || configuredYear === null
    ? applications
    : applications.filter((application) => application.programYear === configuredYear)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">This applicant already has applications</CardTitle>
            <CardDescription>
              Showing {showPreviousYears
                ? "all"
                : configuredYear === null
                  ? configuredYearUnavailable ? "all applications (current configured year unavailable)" : "all applications while the current configured year loads"
                  : `current program year (${configuredYear})`}.
              Resume a draft or start a new application with a previous household. Income, program inputs,
              eligibility, grants and payments are not copied. The original application is unchanged.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => onStartDraft(null)} disabled={starting || !canStart}>
            {starting ? <IconLoader2 className="size-4 animate-spin" /> : <IconFilePlus className="size-4" />}
            New with applicant only
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {visibleApplications.length === 0 ? (
          <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No applications in the current program year. Older applications remain available below.
          </p>
        ) : visibleApplications.map((application) => {
          const action = priorApplicationAction(application.status, canEditCompleted)
          return (
            <div
              key={application.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {enumLabel(benefitAppTypeLabels, application.programType)} {application.programYear}
                    <span className="text-sm font-normal text-muted-foreground">
                      • Applicant: {application.applicantName || "Unavailable"}
                    </span>
                  </span>
                  <Badge variant="secondary" aria-label="Application status">
                    {applicationWorkflowLabel(application)}
                  </Badge>
                  {(application.requestedBenefits ?? []).map((program) => (
                    <Badge key={program.programId} variant="outline" aria-label={`Requested ${program.name}`}>
                      Requested: {program.name}
                    </Badge>
                  ))}
                  {(application.requestedBenefitsReadUnavailable || application.requestedBenefits == null) && (
                    <Badge variant="outline" aria-label="Requested programs unavailable">
                      Requested programs unavailable
                    </Badge>
                  )}
                  {!application.isApplicant && <Badge variant="outline">Household member</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Intake {fmtDate(application.intakeDate)} • {application.peopleInHousehold} in
                  household • Paid {fmtMoney(application.amountPaid)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResume(application.id)}
                  disabled={starting}
                >
                  {action === "resume" ? (
                    <>
                      <IconPlayerPlay className="size-4" />
                      Resume
                    </>
                  ) : (
                    <>
                      <IconEye className="size-4" />
                      View
                    </>
                  )}
                </Button>
                  <Button
                    size="sm"
                    data-copy-source={application.id}
                    onClick={() => application.isApplicant ? void onStartDraft(application.id) : setCopySourceId(application.id)}
                    disabled={starting || !canStart}
                    aria-label={`New application with the same household as ${enumLabel(benefitAppTypeLabels, application.programType)} ${application.programYear}, intake ${fmtDate(application.intakeDate)}`}
                  >
                    <IconUsersGroup className="size-4" />
                    New with same household
                  </Button>
              </div>
            </div>
          )
        })}

        <Separator />
        {!showPreviousYears ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Need to review an older application?</p>
            <Button variant="link" className="px-0" onClick={() => setShowPreviousYears(true)}>
              See Previous Years
            </Button>
          </div>
        ) : (
          <Button variant="link" className="px-0" onClick={() => setShowPreviousYears(false)}>
            Show Current Year Only
          </Button>
        )}
      </CardContent>
      {copyDialog}
    </Card>
  )
}

export default function NewBenefitApplicationPage() {
  const [searchParams] = useSearchParams()
  const store = useAppStore()
  const additionalNavigationBlocked = useCallback(() => creationNavigationBlocked(store.getState().benefitCreation), [store])
  const applicationId = searchParams.get("applicationId")
  const read = useBenefitReadBridge(applicationId ?? undefined)
  const content = <NewBenefitApplicationContent read={read} />
  return <BenefitEditScope additionalNavigationBlocked={additionalNavigationBlocked}>{applicationId
    ? <BenefitEditorWorkspace key={applicationId} benefitId={applicationId} readStamp={read.readStamp} writable={read.current} onRefresh={read.refresh}>{content}</BenefitEditorWorkspace>
    : content}</BenefitEditScope>
}

function NewBenefitApplicationContent({ read }: { read: ReturnType<typeof useBenefitReadBridge> }) {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const creation = useAppSelector(s => s.benefitCreation)
  const session = useAppSelector(s => s.staffSession)
  const hasOpenEditors = useHasOpenBenefitEditors() || creationNavigationBlocked(creation)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const permissions = useAppSelector((state) => state.permissions.permissions)

  const applicantId = searchParams.get("applicantId")
  const applicationId = searchParams.get("applicationId")
  const requestedBenefitApplicationType = searchParams.get("benefitApplicationType")
  const step = resolveStep(searchParams.get("step"), { applicantId, applicationId })
  const heading = useRef<HTMLHeadingElement>(null)
  useLayoutEffect(() => {
    // Entering the saved workspace replaces the pre-root subtree. Focus the committed
    // heading, not the old heading that a navigation-frame callback can race with.
    if (applicationId && creation.attempt?.phase === "confirmed" && creation.attempt.resultId === applicationId) heading.current?.focus()
  }, [applicationId, creation.attempt])

  const { benefit, loading, refresh: setBenefit } = read
  const creatingDraft = creationLocked(creation)
  const capabilityRead = benefitApi.useGetBenefitCreationCapabilityQuery(session.scopeKey ? { scopeKey: session.scopeKey } : skipToken, { refetchOnMountOrArgChange: true })
  const creationCapabilityLoading = capabilityRead.isLoading || capabilityRead.isFetching
  const creationCapability = !capabilityRead.isError && !creationCapabilityLoading ? capabilityRead.currentData ?? null : null

  const goTo = useCallback(
    (next: { step?: CreateBenefitStep; applicantId?: string | null; applicationId?: string | null }) => {
      const params = new URLSearchParams(searchParams)
      const apply = (key: string, value: string | null | undefined) => {
        if (value === undefined) return
        if (value === null) params.delete(key)
        else params.set(key, value)
      }
      apply("applicantId", next.applicantId)
      apply("applicationId", next.applicationId)
      if (next.step) params.set("step", next.step)
      setSearchParams(params)
      requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-creation-heading]')?.focus())
    },
    [searchParams, setSearchParams],
  )

  const openCreated = useCallback((id: string) => {
    if (creation.attempt?.kind === "person") {
      dispatch(creationActions.personAdopted(id))
      goTo({ applicantId: id, step: "applicant" })
    } else goTo({ applicationId: id, step: "household" })
  }, [creation.attempt?.kind, dispatch, goTo])
  // Only a completion observed while mounted can navigate automatically. A result that
  // arrived while unmounted stays in Redux for the explicit Continue-to-saved action.
  const observedAttempt = useRef<string | null>(null)
  useEffect(() => {
    const attempt = creation.attempt
    if (attempt?.phase === "saving") observedAttempt.current = attempt.id
    if (attempt?.phase !== "confirmed" || observedAttempt.current !== attempt.id || !attempt.resultId || applicationId) return
    if (attempt.kind === "person" ? !applicantId : applicantId === attempt.body.applicantId) openCreated(attempt.resultId)
  }, [creation.attempt, applicationId, applicantId, openCreated])

  useEffect(() => {
    // PSE handoff supplies a durable id, not an invented applicant. Use the checked snapshot.
    if (applicationId && !applicantId && benefit?.applicantId) {
      goTo({ applicantId: benefit.applicantId, applicationId, step: "household" })
    }
  }, [applicationId, applicantId, benefit?.applicantId, goTo])

  const startDraft = useCallback(
    async (copyHouseholdFromBenefitApplicationId: string | null, confirmation?: HouseholdCopyConfirmation): Promise<boolean> => {
      if (!applicantId || !session.scopeKey || creationLocked(store.getState().benefitCreation)) return false
      if (!creationCapability?.canCreate) {
        toast.error(creationCapability?.disabledReason ?? "The server did not authorize new-root creation.")
        return false
      }
      const eligibility = creation.applicantEligibility?.personId === applicantId ? creation.applicantEligibility.value : null
      // Original named endpoint/payload. A local attempt ID adds no server idempotency.
      dispatch(creationActions.attemptRequested({ id: crypto.randomUUID(), scopeKey: session.scopeKey, epoch: session.epoch,
        kind: "application", phase: "saving", resultId: null, body: {
          applicantId,
          ...(requestedBenefitApplicationType && /^\d+$/.test(requestedBenefitApplicationType) ? { benefitApplicationType: Number(requestedBenefitApplicationType) } : {}),
          ...(copyHouseholdFromBenefitApplicationId ? { copyHouseholdFromBenefitApplicationId, ...confirmation }
            : eligibility !== null ? { applicantLiheapEligibility: eligibility } : {}),
        } }))
      // The listener owns completion/navigation. Never close a pending confirmation on dispatch.
      return false
    },
    [applicantId, creation.applicantEligibility, creationCapability, dispatch, requestedBenefitApplicationType, session, store],
  )

  const legacyCanEdit = benefit !== null && isBenefitEditable(benefit.proofingStatus, permissions.includes("benefit-applications:edit"))
  const canEdit = (section: string) => read.current && workflowCanEdit(read.workflow, section, legacyCanEdit)

  const finish = () => {
    if (applicationId) navigate(`/benefit-applications/${applicationId}?section=program-details`)
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4 md:gap-6 md:py-6 lg:px-6">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} disabled={hasOpenEditors} className="mt-0.5">
          <IconArrowLeft className="size-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 ref={heading} data-creation-heading tabIndex={-1} className="text-2xl font-bold">New Benefit Application</h1>
            {applicationId && <Badge variant="secondary">Draft saved</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Universal (Combined) application. Save each editor before continuing. Saved drafts can
            be resumed from the applicant's file.
          </p>
        </div>
      </div>

      <CreationCapabilityNotice capability={creationCapability} loading={creationCapabilityLoading} />
      {capabilityRead.isError && <p role="alert">Creation authority is unavailable. <Button variant="outline" onClick={() => void capabilityRead.refetch()}>Retry creation check</Button></p>}
      {(!applicationId || step === "applications" || step === "applicant") && <BenefitCreationStatus onOpenCreated={openCreated} />}
      <StepIndicator current={step} />
      {applicationId && (read.error || read.refreshUnavailable) && <p role="alert">Saved application state is unavailable. Editing remains disabled. <Button variant="outline" onClick={read.refresh}>Retry workspace read</Button></p>}
      {hasOpenEditors && <p role="status" className="text-sm font-medium">Save or cancel the open editor before leaving this step.</p>}
      {benefit && <HouseholdCopyNotice benefit={benefit} />}
      {benefit && <ImportedPseSourceNotice benefit={benefit} />}
      <Separator />

      {step === "applicant" && (
        <ApplicantStep
          applicantId={applicantId}
          onSelect={(personId) => {
            if (creationLocked(store.getState().benefitCreation)) return
            dispatch(creationActions.applicantSelected())
            goTo({ applicantId: personId, step: "applicant" })
          }}
          onClear={() => {
            if (creationLocked(store.getState().benefitCreation)) return
            dispatch(creationActions.applicantSelected())
            goTo({ applicantId: null, applicationId: null, step: "applicant" })
          }}
        />
      )}

      {step === "applications" && applicantId && (
        <ExistingApplicationsStep
          applicantId={applicantId}
          canEditCompleted={permissions.includes("benefit-applications:edit")}
          starting={creatingDraft}
          onResume={(id) => navigate(`/benefit-applications/${id}`)}
          onStartDraft={startDraft}
          creationCapability={creationCapability}
        />
      )}

      {(step === "household" || step === "demographics" || step === "income") && loading && (
        <div className="flex flex-col items-center gap-3 py-16">
          <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading the draft application…</p>
        </div>
      )}

      {step === "household" && benefit && (
        <div className="space-y-4">
          <ApplicationDetailsSection benefit={benefit} canEdit={canEdit("Household")} onUpdated={setBenefit} />
          <TargetProgramYearSection benefit={benefit} canEdit={canEdit("Program")} onUpdated={setBenefit} />
          <IntakeDetailsSection benefit={benefit} canEdit={canEdit("Household")} canEditProgram={canEdit("Program")} onUpdated={setBenefit} />
          <HouseholdInformationSection benefit={benefit} canEdit={canEdit("Household")} onUpdated={setBenefit} />
          <NonCashBenefitsSection benefit={benefit} canEdit={canEdit("Household")} onUpdated={setBenefit} />
          <QuestionnaireSection benefit={benefit} canEdit={canEdit("Household") && canEdit("Program")} onUpdated={setBenefit} />
        </div>
      )}

      {step === "demographics" && benefit && (
        <HouseholdMembersSection benefit={benefit} canEdit={canEdit("Demographics")} onUpdated={setBenefit} />
      )}

      {step === "income" && benefit && (
        <HouseholdIncomeSection benefit={benefit} canEdit={canEdit("Income")} onUpdated={setBenefit} />
      )}

      <Separator />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {step === "applications" && (
            <Button variant="outline" disabled={hasOpenEditors} onClick={() => goTo({ step: "applicant" })}>
              <IconArrowLeft className="size-4" />
              Back
            </Button>
          )}
          {step === "household" && (
            <Button variant="outline" disabled={hasOpenEditors} onClick={() => goTo({ step: "applications" })}>
              <IconArrowLeft className="size-4" />
              Back
            </Button>
          )}
          {(step === "demographics" || step === "income") && (
            <Button variant="outline" disabled={hasOpenEditors} onClick={() => goTo({ step: step === "income" ? "demographics" : "household" })}>
              <IconArrowLeft className="size-4" />
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {step === "applicant" && (
            <Button disabled={!applicantId || hasOpenEditors} onClick={() => goTo({ step: "applications" })}>
              Continue
              <IconArrowRight className="size-4" />
            </Button>
          )}
          {(step === "household" || step === "demographics") && (
            <Button disabled={hasOpenEditors} onClick={() => goTo({ step: step === "household" ? "demographics" : "income" })}>
              Continue
              <IconArrowRight className="size-4" />
            </Button>
          )}
          {step === "income" && (
            <Button disabled={hasOpenEditors} onClick={finish}>
              <IconCheck className="size-4" />
              Continue to programs
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
